import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from './LoadingSpinner';
import { ConnectionErrorScreen } from './ConnectionErrorScreen';

/**
 * RequireSuperAdmin Component
 *
 * Route protection component that restricts access to Super Admin users only.
 * Redirects to login if not authenticated, or home if not a super admin.
 *
 * @example
 * <Route path="/admin" element={<RequireSuperAdmin><AdminLayout /></RequireSuperAdmin>} />
 */

interface RequireSuperAdminProps {
  children: React.ReactNode;
}

export const RequireSuperAdmin = ({ children }: RequireSuperAdminProps) => {
  const { isAuthenticated, isLoading, user, authConnectionError } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  // Transient connectivity failure during the boot-time auth check — the
  // session may still be valid, so do NOT redirect to /login. Let the user
  // retry instead of silently discarding a valid session.
  if (authConnectionError && !isAuthenticated) {
    return <ConnectionErrorScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to home if not a super admin
  if (!user?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and is a super admin, render children
  return <>{children}</>;
};
